import { Combobox } from '@headlessui/react';
import { useRef, useState } from 'react';
import { FilterOption } from './colabOptions';

type ColabCategoryComboboxProps = {
  id: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  emptyOptionLabel?: string;
  className?: string;
  optionsClassName?: string;
};

function classNames(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

function getOptionLabel(options: FilterOption[], value: string, emptyOptionLabel?: string) {
  if (value === '') return emptyOptionLabel || '';
  return options.find(option => option.value === value)?.label || value;
}

function getMatchScore(option: FilterOption, normalizedQuery: string) {
  const normalizedLabel = option.label.toLowerCase();
  if (normalizedLabel === normalizedQuery) return 0;
  if (normalizedLabel.startsWith(normalizedQuery)) return 1;
  if (normalizedLabel.split(/\s+/).some(word => word.startsWith(normalizedQuery))) return 2;
  return 3;
}

function getMatchingOptions(options: FilterOption[], normalizedQuery: string) {
  if (normalizedQuery === '') return options;

  return options
    .filter(option => option.label.toLowerCase().includes(normalizedQuery))
    .sort((firstOption, secondOption) => {
      const scoreDifference =
        getMatchScore(firstOption, normalizedQuery) - getMatchScore(secondOption, normalizedQuery);

      return scoreDifference || firstOption.label.localeCompare(secondOption.label);
    });
}

function ColabCategoryCombobox({
  id,
  value,
  options,
  onChange,
  disabled = false,
  placeholder = 'Choose',
  emptyOptionLabel,
  className,
  optionsClassName,
}: ColabCategoryComboboxProps) {
  const [query, setQuery] = useState('');
  const [isInputActive, setIsInputActive] = useState(false);
  const hasCommittedSelection = useRef(false);
  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = getMatchingOptions(options, normalizedQuery);

  function handleChange(nextValue: string) {
    hasCommittedSelection.current = true;
    setQuery('');
    setIsInputActive(false);
    onChange(nextValue);
  }

  function handleBlur() {
    if (hasCommittedSelection.current) {
      hasCommittedSelection.current = false;
      setQuery('');
      setIsInputActive(false);
      return;
    }

    if (query.trim() && filteredOptions[0]) {
      onChange(filteredOptions[0].value);
    }

    setQuery('');
    window.setTimeout(() => setIsInputActive(false), 100);
  }

  function handleFocus(event: React.FocusEvent<HTMLInputElement>) {
    setIsInputActive(true);
    event.target.select();
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    hasCommittedSelection.current = false;
    setIsInputActive(true);
    setQuery(event.target.value);
  }

  return (
    <Combobox value={value} onChange={handleChange} disabled={disabled}>
      <div className="colab-combobox">
        <Combobox.Input
          id={id}
          className={classNames('colab-field-control colab-combobox__input', className)}
          displayValue={(selectedValue: string) => getOptionLabel(options, selectedValue, emptyOptionLabel)}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
        />
        <Combobox.Button
          className="colab-combobox__button"
          aria-label="Open category options"
          onMouseDown={() => setIsInputActive(true)}>
          <svg className="colab-combobox__chevron" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
              clipRule="evenodd"
            />
          </svg>
        </Combobox.Button>
        <Combobox.Options static={isInputActive} className={classNames('colab-combobox__options', optionsClassName)}>
          {emptyOptionLabel && normalizedQuery === '' && (
            <Combobox.Option className="colab-combobox__option" value="">
              {emptyOptionLabel}
            </Combobox.Option>
          )}
          {filteredOptions.map(({ id: optionId, label, value: optionValue }) => (
            <Combobox.Option key={optionId} className="colab-combobox__option" value={optionValue}>
              <span className="colab-combobox__option-label">{label}</span>
            </Combobox.Option>
          ))}
          {filteredOptions.length === 0 && <div className="colab-combobox__empty">No matches</div>}
        </Combobox.Options>
      </div>
    </Combobox>
  );
}

export default ColabCategoryCombobox;
